import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PagamentiPendentiContext = createContext();

export function PagamentiPendentiProvider({ children }) {
  const { user } = useAuth();
  const [clientiConPendenti, setClientiConPendenti] = useState([]);
  const [clientiIds, setClientiIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const fetchPagamentiPendenti = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/abbonamenti/clienti-non-pagati`);
      const data = res.data || [];
      setClientiConPendenti(data);
      setClientiIds(new Set(data.map(c => c.cliente_id)));
    } catch (error) {
      console.error('[PagamentiPendenti] Errore:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchPagamentiPendenti();
    } else {
      setClientiConPendenti([]);
      setClientiIds(new Set());
      setLoading(false);
    }

    // Refresh ogni 5 minuti
    const interval = setInterval(() => {
      if (user) fetchPagamentiPendenti();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, fetchPagamentiPendenti]);

  const hasPagamentoPendente = useCallback((clienteId) => {
    if (!clienteId) return false;
    return clientiIds.has(clienteId);
  }, [clientiIds]);

  const getPagamentoPendenteInfo = useCallback((clienteId) => {
    return clientiConPendenti.find(c => c.cliente_id === clienteId);
  }, [clientiConPendenti]);

  const refresh = useCallback(() => {
    fetchPagamentiPendenti();
  }, [fetchPagamentiPendenti]);

  const value = useMemo(() => ({
    clientiConPendenti,
    clientiIds,
    loading,
    hasPagamentoPendente,
    getPagamentoPendenteInfo,
    refresh
  }), [clientiConPendenti, clientiIds, loading, hasPagamentoPendente, getPagamentoPendenteInfo, refresh]);

  return (
    <PagamentiPendentiContext.Provider value={value}>
      {children}
    </PagamentiPendentiContext.Provider>
  );
}

export function usePagamentiPendenti() {
  const context = useContext(PagamentiPendentiContext);
  if (!context) {
    // Return safe defaults if not in provider
    return {
      clientiConPendenti: [],
      clientiIds: new Set(),
      loading: false,
      hasPagamentoPendente: () => false,
      getPagamentoPendenteInfo: () => null,
      refresh: () => {}
    };
  }
  return context;
}
